import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useCore } from "@spectral-finance/react-core";

import { onboardCss } from "../styles/web3onboard";
import { shortenAddress } from "../utils";

export enum DialogSteps {
  ONBOARDING = 0,
  FAQ = 1,
  WALLET_CONNECTION = 2,
  BUNDLE = 3,
  SUCCESS = 4,
}

export type DialogStepsContext = {
  onboardElement: Element;
  currentStep: DialogSteps;
  setCurrentStep: (step: DialogSteps, forceSwitch?: boolean) => void;
  forceingSwitchWallet: boolean;
  clearForceingSwitchWallet: () => void;
  connectWallet: () => void;
  addNewWallet: () => void;
  loadingSignature: boolean;
};

const useTrigger = () => {
  const [triggerNumber, setNext] = useState(0);
  const trigger = useCallback(() => {
    setTimeout(() => setNext(Math.random()), 0);
  }, []);
  return [triggerNumber, trigger] as const;
};

const DialogStepsContext = createContext<DialogStepsContext>({} as any);

const DialogStepsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { login, account, bundle, web3 } = useCore();

  const [currentStep, setCurrentStep] = useState<DialogSteps>(
    DialogSteps.ONBOARDING
  );
  const [forceingSwitchWallet, forceSwitchWallet] = useState(false);
  const [onboardElement, setOnboardElement] = useState<Element>(
    document.createElement("div")
  );

  const [nextCheckLogin, triggerLogin] = useTrigger();
  const [nextCheckBundle, triggerBundle] = useTrigger();

  // Change step behavior
  const navigateStep = useCallback(
    (step: DialogSteps) => {
      if (
        currentStep === DialogSteps.ONBOARDING &&
        step === DialogSteps.WALLET_CONNECTION &&
        account.isInBundleInProgress
      ) {
        setCurrentStep(DialogSteps.BUNDLE);
        return;
      }
      setCurrentStep(step);
    },
    [setCurrentStep, account.address, account.isInBundleInProgress]
  );

  // Bundle status on logged account
  useEffect(() => {
    let completed = false
    bundle.quickCreationOrAddition(shortenAddress(account.address))
      .then(() => {
        if (!completed) {
          completed = true
        } else {
          setCurrentStep(DialogSteps.BUNDLE);
        }
      });
    setTimeout(() => {
      if (completed) {
        setCurrentStep(DialogSteps.BUNDLE);
      } else {
        completed = true
      }
    }, 1800);
  }, [nextCheckBundle]);

  // When an wallet is connected but not logged try to login
  const [loadingSignature, setLoadingSignature] = useState<boolean>(false);
  useEffect(() => {
    if (account.address && !account.logged) {
      setLoadingSignature(true);
      const timeout = setTimeout(async () => {
        if (!web3.isChainId) {
          try {
            if (!await web3.forceChain()) {
              throw new Error();
            }
          } catch {
            setCurrentStep(DialogSteps.ONBOARDING);
            setLoadingSignature(false);
            return;
          }
        }
        login()
          .then(() => {
            setCurrentStep(DialogSteps.BUNDLE);
            setLoadingSignature(false);
            setTimeout(() => triggerBundle(), 100);
          })
          .catch((error) => {
            setLoadingSignature(false);
            setCurrentStep(DialogSteps.ONBOARDING);
          });
      }, 1800);
      return () => clearTimeout(timeout);
    }
    if (account.address && account.isInBundleInProgress && !forceSwitchWallet) {
      const timeout = setTimeout(() => {
        setCurrentStep(DialogSteps.BUNDLE);
      }, 1800);
      return () => clearTimeout(timeout);
    }
    if (account.address && account.logged && !account.isInBundleInProgress) {
      const timeout = setTimeout(() => {
        triggerBundle();
      }, 1800);
      return () => clearTimeout(timeout);
    }
  }, [nextCheckLogin, triggerBundle]);

  useEffect(() => {
    if (currentStep === DialogSteps.WALLET_CONNECTION) {
      triggerLogin();
    }
  }, [account.address, account.logged, currentStep]);

  // Connect wallets
  const connectWallet = useCallback(() => {
    if (!forceingSwitchWallet) {
      web3.connect().then(triggerLogin);
    } else {
      web3.switchWallet();
    }
  }, [forceingSwitchWallet, triggerLogin, web3.connect, web3.switchWallet]);

  // Store onboard element
  useEffect(() => {
    const onboardStyles = document.createElement("style");
    onboardStyles.innerHTML = onboardCss;
    const onboard = document.querySelector("onboard-v2")!;
    onboard.shadowRoot!.appendChild(onboardStyles);
    setOnboardElement(onboard);
  }, []);

  return (
    <DialogStepsContext.Provider
      value={{
        onboardElement,

        currentStep,
        setCurrentStep: navigateStep,

        connectWallet,
        addNewWallet: () => {
          forceSwitchWallet(true);
          setCurrentStep(DialogSteps.WALLET_CONNECTION);
        },
        forceingSwitchWallet,
        clearForceingSwitchWallet: () => forceSwitchWallet(false),

        loadingSignature,
      }}
    >
      {children}
    </DialogStepsContext.Provider>
  );
};

export const useDialogStepsContext = () => useContext(DialogStepsContext);
export { DialogStepsProvider };
